import { appRoutes } from "@main/router/apps";
import { defineStore } from "pinia";
import { ref } from "vue";
import { useRouter, type RouteLocationNormalizedLoadedGeneric } from "vue-router";

export interface TabRecord {
	key: string;
	title: string;
	fullPath: string;
	logo?: string;
}

export interface AppItem {
	title: string;
	logo: string;
	path: string;
	fullPath?: string;
}

export const useAppTabsStore = defineStore(
	"appTabs",
	() => {
		const activeKey = ref<Key>("");
		const tabs = ref<TabRecord[]>([]);
		const router = useRouter();

		const setActiveKey = (key: string) => {
			activeKey.value = key;
		};

		// 添加标签
		function addTab(app: AppItem, jump = true) {
			const { title, logo, path, fullPath = "" } = app;

			const index = tabs.value.findIndex((item) => item.key === path);

			if (index > -1) {
				// // 如果标签已存在，则直接打开应用
				activeKey.value = path;
				router.push(tabs.value[index].fullPath);

				return;
			}

			// 如果标签数量超过最大值，则删除最早添加的标签
			if (tabs.value.length >= 10) {
				tabs.value.shift();
			}

			// 添加标签
			tabs.value.push({
				key: path,
				title,
				fullPath: fullPath || path,
				logo,
			});

			activeKey.value = path;

			if (jump) {
				router.push(fullPath || path);
			}
		}

		// 删除标签
		function removeTab(key: string) {
			const isRemovingActiveTab = String(activeKey.value) === key;

			tabs.value = tabs.value.filter((item) => item.key !== key);

			if (isRemovingActiveTab) {
				activeKey.value = "";
			}
		}

		// 清空标签
		function clearTabs() {
			tabs.value = [];
			activeKey.value = "";
		}

		function switchToActiveTab() {
			const activeTab = tabs.value.find((tab) => tab.key === activeKey.value);

			if (activeTab) {
				router.push(activeTab.fullPath);
			}
		}

		function backDesktop() {
			const route = router.currentRoute.value;
			const index = tabs.value.findIndex((item) => item.key === activeKey.value);

			if (index > -1) {
				// 更新当前标签的 fullPath
				tabs.value[index].fullPath = route.fullPath;
			}

			activeKey.value = "";
			router.push("/desktopCenter");
		}

		function leaveDesktop() {
			if (!activeKey.value) {
				return;
			}

			const index = tabs.value.findIndex((item) => item.key === activeKey.value);

			if (index > -1) {
				// 更新当前标签的 fullPath
				tabs.value[index].fullPath = router.currentRoute.value.fullPath;
			}
		}

		function closeCurrentTab() {
			if (!activeKey.value) {
				return;
			}

			const index = tabs.value.findIndex((item) => item.key === activeKey.value);

			if (index > -1) {
				const leftTab = tabs.value[index - 1];
				const rightTab = tabs.value[index + 1];
				const nextTab = leftTab || rightTab;

				if (nextTab) {
					activeKey.value = nextTab.key;
				}

				tabs.value.splice(index, 1);

				if (!nextTab) {
					activeKey.value = "";
					router.push("/desktopCenter");

					return;
				}

				router.push(nextTab.fullPath);
			}
		}

		function updateActiveTabFullPath(route: RouteLocationNormalizedLoadedGeneric) {
			if (!activeKey.value) {
				return;
			}

			const newFullPath = route.fullPath;
			const index = tabs.value.findIndex((item) => item.key === activeKey.value);

			if (index > -1) {
				const key = tabs.value[index].key;

				if (newFullPath.includes(key)) {
					// 更新当前标签的 fullPath
					tabs.value[index].fullPath = newFullPath;
				} else {
					const findRoute = appRoutes.find((r) => {
						return r.children?.some((child) => child.path === route.path);
					});

					if (findRoute) {
						addTab(
							{
								path: findRoute.path || "",
								title: findRoute?.meta?.title || "",
								logo: findRoute?.meta?.logo || "",
								fullPath: newFullPath,
							},
							false
						);
					}
				}
			}
		}

		return {
			activeKey,
			tabs,
			setActiveKey,
			addTab,
			removeTab,
			clearTabs,
			switchToActiveTab,
			backDesktop,
			leaveDesktop,
			closeCurrentTab,
			updateActiveTabFullPath,
		};
	},
	{
		// 开启数据持久化
		persist: [
			{
				storage: sessionStorage,
			},
		],
	}
);
